import React, { useState, useEffect, useCallback } from 'react';
import { useTheme } from '@mui/material/styles';
import { getStyles } from '../../../theme/styles';
import { InputLabel, MenuItem, Select, FormControl } from '@mui/material';
import testAheadService from '../../../services/TestAheadService';
import { useCourseState, useCourseDispatch, CourseActionType } from '../../../context/CourseContext';

interface ProvisionSelectProps {
  courseCode?: string;
  onChange: any;
  disabled?: boolean;
  variant?: 'filled' | 'outlined';
  className?: string;
}

export default function TestAheadCourseCodeSelector({ courseCode, onChange, disabled, variant, className }: ProvisionSelectProps) {
  const theme = useTheme();
  const styles = getStyles(theme);
  const { testAheadCourseSaved, site } = useCourseState();

  const [courseTitles, setCourseTitles] = useState<string[]>([]);
  const [selectedCourseCode, setSelectedCourseCode] = useState<string | undefined>(undefined);
  const memoOnChange = useCallback(onChange, []);
  const courseDispatch = useCourseDispatch();

  
  useEffect(() => {
    const fetchCourses = async () => {
      const data = await testAheadService.getTestAheadCourses(site?.id);
      if (data) {
        const titles = Array.from(new Set(data.map(course => course.courseCode)));
        setCourseTitles(titles);
        if(titles.length === 1) {
          setSelectedCourseCode(titles[0]);
        }
        if(data.length === 1) {
          memoOnChange(data[0]);
        }
      }
    };

    const resetState = () => {
      courseDispatch({
        type: CourseActionType.TestAheadCourseSaved,
        testAheadCourseSaved: false,
      })
    }
    fetchCourses();
    resetState();

  }, [memoOnChange, site, testAheadCourseSaved, courseDispatch]);

  useEffect(() => {
    if(courseTitles && courseTitles.length && courseCode && courseCode !== selectedCourseCode) {      
      setSelectedCourseCode(courseCode);
    }
    if(courseCode === '') {
      setSelectedCourseCode(undefined);
    }
  }, [courseTitles, courseCode, selectedCourseCode]);

  const handleSelectedCourseCodeChange = (event: any) => {
    setSelectedCourseCode(event.target.value);
    onChange(event.target.value)
  }

  return (
    <FormControl
      sx={className ? undefined : styles.pFormControl}
      className={className}
      variant={ variant || "standard" }
    >
      <InputLabel id='courseCodeLabel'>
        { courseTitles && courseTitles.length ? "Course Code": "No Course Codes Found"}
      </InputLabel>
      <Select
        variant="standard"
        data-testid="test-courseCode"
        value={selectedCourseCode || ''}
        onChange={handleSelectedCourseCodeChange}
        name="courseCode"
        id="courseCode"
        labelId='courseCodeLabel'
        role="option"
        disabled={!courseTitles || courseTitles.length <= 1 || disabled}>
        {courseTitles.length &&
          courseTitles.map((title: any) => (
            <MenuItem key={title} value={title} role="option">
              {title}
            </MenuItem>
          ))}
      </Select>
    </FormControl>
  );
}
